import { CLIENT_ID, useEnvConfig } from "@/env-config";
import { GUID } from "@faro-lotv/foundation";
import { AuthContext, useAuthContext } from "@faro-lotv/gate-keepers";
import { useCoreApiTokenProvider } from "@faro-lotv/service-wires";
import { PropsWithChildren, useEffect, useMemo } from "react";

/**
 * @returns a wrapper that ensures that the current user is logged in to the specified environment and region.
 */
export function HBEnsureLogin({
  children,
}: PropsWithChildren): JSX.Element | null {
  const { isLoggedIn, requestLogin } = useAuthContext();

  useEffect(() => {
    requestLogin();
  }, [requestLogin]);

  if (!isLoggedIn) {
    return null;
  }

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>{children}</>
  );
}

type ProjectAccessProps = {
  projectId: GUID;
};

/** @returns a wrapper that augment the auth context with this project context provider */
export function HBProjectAccess({
  projectId,
  children,
}: PropsWithChildren<ProjectAccessProps>): JSX.Element {
  const env = useEnvConfig();
  const origContext = useAuthContext();

  const tokenProvider = useCoreApiTokenProvider({
    coreApiBaseUrl: env.coreApiUrl,
    projectId,
    clientId: CLIENT_ID,
  });

  const value = useMemo<AuthContext>(
    () => ({
      ...origContext,
      tokenProvider,
    }),
    [origContext, tokenProvider],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
